import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { exportPdf } from "@/lib/pdf";

export function useDocEditor(id) {
  const api = useApi();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [doc, setDoc] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const [pricingOpen, setPricingOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const dirty = useRef(false);
  const latest = useRef(null);
  const autoDone = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get(`/documents/${id}`)
      .then((r) => {
        // Ignore stale responses and never overwrite unsaved local edits
        if (!alive || (dirty.current && latest.current?.id === id)) return;
        latest.current = r.data; setDoc(r.data);
      })
      .catch(() => { if (alive) { toast.error("Document not found"); navigate("/app"); } });
    return () => { alive = false; };
  }, [id, api, navigate]);

  const update = (patch) => {
    setDoc((d) => { const n = { ...d, ...patch }; latest.current = n; return n; });
    dirty.current = true;
    setSaveState("unsaved");
  };

  const patchLocal = (patch) => setDoc((d) => { const n = { ...d, ...patch }; latest.current = n; return n; });

  const save = useCallback(async () => {
    if (!dirty.current || !latest.current) return;
    dirty.current = false;
    setSaveState("saving");
    const d = latest.current;
    try {
      await api.put(`/documents/${id}`, { title: d.title || "Untitled", template: d.template, data: d.data, ...(d.accent ? { accent: d.accent } : {}) });
      setSaveState("saved");
    } catch {
      dirty.current = true;
      setSaveState("error");
    }
  }, [api, id]);

  useEffect(() => {
    if (saveState !== "unsaved") return;
    const t = setTimeout(save, 900);
    return () => clearTimeout(t);
  }, [doc, saveState, save]);

  const download = async () => {
    const d = latest.current;
    if (!d?.can_download) return setPricingOpen(true);
    setExporting(true);
    try {
      await exportPdf(d.type, d);
      toast.success("Download ready");
    } catch (e) {
      console.error(e);
      toast.error("Could not create the PDF");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!doc || autoDone.current || params.get("download") !== "1") return;
    autoDone.current = true;
    setParams({}, { replace: true });
    if (doc.can_download) setTimeout(download, 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  return { api, doc, update, patchLocal, save, saveState, download, exporting, pricingOpen, setPricingOpen };
}
